import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Calendar, Clock3, CheckCircle2, TrendingUp } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Market } from "@/types/market";

interface MarketCardProps {
  market: Market;
}

function formatCountdown(endDate: string) {
  const now = Date.now();
  const end = new Date(endDate).getTime();
  const diff = end - now;

  if (diff <= 0) return "Closed";

  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  if (minutes > 0) return `${minutes}m left`;
  return "Closing now";
}

function formatPolVolume(volume: number) {
  if (!Number.isFinite(volume)) return "0.000";
  return volume.toFixed(3);
}

function formatPercent(value: number) {
  if (!Number.isFinite(value)) return "0.0";
  return (value * 100).toFixed(1);
}

function formatPrice(value: number) {
  if (!Number.isFinite(value)) return "0.00";
  return value.toFixed(2);
}

const MarketCard = ({ market }: MarketCardProps) => {
  const { t, i18n } = useTranslation();
  const question = i18n.language === "en" ? market.question_en : market.question_gr;

  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    const interval = window.setInterval(() => {
      setNowTick(Date.now());
    }, 15_000);

    return () => window.clearInterval(interval);
  }, []);

  const isResolved = market.status === "resolved";
  const isClosedByTime = new Date(market.end_date).getTime() <= nowTick;
  const isOpen = !isResolved && !isClosedByTime;

  const statusLabel = isResolved ? "Resolved" : isClosedByTime ? "Closed" : "Open";

  const countdownText = useMemo(() => {
    if (isResolved) return "Resolved";
    return formatCountdown(market.end_date);
  }, [market.end_date, isResolved, nowTick]);

  const yesPercent = formatPercent(market.yes_price);
  const noPercent = formatPercent(market.no_price);

  return (
    <Link
      to={`/market/${market.id}`}
      className="group block rounded-[28px] border border-border/70 bg-card p-6 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <span className="inline-flex rounded-full border border-border/70 bg-background/80 px-3 py-1 text-[11px] font-medium text-muted-foreground">
          {t(`categories.${market.category}`)}
        </span>

        <span
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${
            isResolved
              ? "border border-sky-500/20 bg-sky-500/10 text-sky-600"
              : isClosedByTime
              ? "border border-slate-500/20 bg-slate-500/10 text-slate-600"
              : "border border-emerald-500/20 bg-emerald-500/10 text-emerald-600"
          }`}
        >
          {isResolved ? <CheckCircle2 className="h-3 w-3" /> : <Clock3 className="h-3 w-3" />}
          {statusLabel}
        </span>
      </div>

      <h3 className="mb-3 text-[20px] font-semibold leading-tight tracking-tight text-card-foreground">
        {question}
      </h3>

      <div className="mb-4 text-xs text-muted-foreground">
        Resolution source: <span className="font-medium text-foreground">{market.resolution_source}</span>
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl border border-border/70 bg-background/60 p-4">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Closes</div>
          <div className="mt-2 text-sm font-semibold text-foreground">{countdownText}</div>
          <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <Calendar className="h-3 w-3" />
            {format(new Date(market.end_date), "dd MMM yyyy")}
          </div>
        </div>

        <div className="rounded-2xl border border-border/70 bg-background/60 p-4">
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Liquidity</div>
          <div className="mt-2 text-sm font-semibold text-foreground">{formatPolVolume(market.volume)} POL</div>
          <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <TrendingUp className="h-3 w-3" />
            Market #{market.id}
          </div>
        </div>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 p-4">
          <div className="text-[11px] uppercase tracking-[0.14em] text-emerald-700/80">
            {t("markets.yes")}
          </div>
          <div className="mt-2 text-2xl font-semibold text-emerald-700">{yesPercent}%</div>
          <div className="mt-1 text-xs text-emerald-700/80">{formatPrice(market.yes_price)} POL</div>
        </div>

        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 p-4">
          <div className="text-[11px] uppercase tracking-[0.14em] text-rose-700/80">
            {t("markets.no")}
          </div>
          <div className="mt-2 text-2xl font-semibold text-rose-700">{noPercent}%</div>
          <div className="mt-1 text-xs text-rose-700/80">{formatPrice(market.no_price)} POL</div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground">
          {isOpen ? "Open for trading" : isResolved ? "Resolved market" : "Trading closed"}
        </div>

        <div
          className={`rounded-full px-4 py-2 text-sm font-medium transition ${
            isOpen ? "bg-foreground text-background group-hover:opacity-90" : "border border-border/70 bg-background text-foreground"
          }`}
        >
          {isOpen ? "Trade market" : "View market"}
        </div>
      </div>
    </Link>
  );
};

export default MarketCard;
